import { Controller } from '@hotwired/stimulus'
import { showFlash } from '../utils/flash_messages'

/**
 * CartShareController
 * 
 * Handles sharing/unsharing a cart via native fetch API.
 * Updates the UI to toggle between share and unshare buttons.
 */
export default class extends Controller {
  static values = {
    shareText: String,
    unshareText: String,
    badgeText: String,
    unsaveText: String,
    unsaveAndUnshareText: String
  }

  /**
   * Share or unshare cart
   * @param {Event} event - Click event
   */
  async toggle(event) {
    const cartId = event.params.cartId
    const shouldShare = event.params.share // true to share, false to unshare
    const endpoint = `/profile/saved_carts/${cartId}`
    
    console.log('Toggling share:', { cartId, shouldShare, endpoint })
    
    try {
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({ shared: shouldShare })
      })
      
      console.log('Response status:', response.status)
      
      const data = await response.json()
      
      console.log('Response data:', data)
      
      if (response.ok && data.success) {
        this.handleSuccess(data, shouldShare)
      } else {
        this.handleError(data)
      }
    } catch (error) {
      console.error('Error toggling cart share:', error)
      this.showFlashMessage('An error occurred', 'danger')
    }
  }

  /**
   * Handle successful share/unshare
   * @param {Object} data - Response data
   * @param {boolean} shouldShare - Whether we were sharing or unsharing
   */
  handleSuccess(data, shouldShare) {
    // Update the badge in the cart header
    this.updateCartBadge(data.shared)
    
    // Toggle the button text and action
    this.toggleButton(data.shared)
    
    // Update the unsave button text
    this.updateUnsaveButton(data.shared)
    
    // Show flash message
    this.showFlashMessage(data.message, 'success')
  }

  /**
   * Update the cart header badge
   * @param {boolean} isShared - Whether cart is now shared
   */
  updateCartBadge(isShared) {
    // Find the badge within the same card container as the button
    const cardContainer = this.element.closest('.bg-white')
    const displayDiv = cardContainer?.querySelector('[data-cart-name-edit-target="display"]')
    
    if (displayDiv) {
      // Find existing shared badge (look for badge with "Shared" text or bi-share-fill icon)
      const existingBadge = Array.from(displayDiv.querySelectorAll('.badge')).find(badge => 
        badge.textContent.includes('Shared') || badge.querySelector('.bi-share-fill')
      )
      
      if (isShared) {
        // Add badge if it doesn't exist
        if (!existingBadge) {
          const badge = document.createElement('span')
          badge.className = 'badge bg-info text-dark ms-2'
          badge.innerHTML = '<i class="bi bi-share-fill"></i> ' + this.badgeTextValue
          // Insert after the h3 element
          const h3 = displayDiv.querySelector('h3')
          if (h3) {
            h3.insertAdjacentElement('afterend', badge)
          }
        }
      } else {
        // Remove badge if it exists
        if (existingBadge) {
          existingBadge.remove()
        }
      }
    }
    
    // Legacy: Update old cart-badge element if it exists (for show page)
    const cartBadge = document.getElementById('cart-badge')
    if (cartBadge) {
      if (isShared) {
        cartBadge.innerHTML = `<span class="badge bg-info text-dark ms-2"><i class="bi bi-share-fill"></i> ${this.badgeTextValue}</span>`
      } else {
        cartBadge.innerHTML = ''
      }
    }
    
    // Legacy: Update cart-name element (for show page)
    const cartNameElement = document.getElementById('cart-name')
    if (cartNameElement) {
      const existingBadge = cartNameElement.parentElement.querySelector('.badge')
      
      if (isShared) {
        if (!existingBadge) {
          const badge = document.createElement('span')
          badge.className = 'badge bg-info text-dark ms-2'
          badge.innerHTML = '<i class="bi bi-share-fill"></i> ' + this.badgeTextValue
          cartNameElement.insertAdjacentElement('afterend', badge)
        }
      } else {
        if (existingBadge) {
          existingBadge.remove()
        }
      }
    }
  }

  /**
   * Toggle the share/unshare button
   * @param {boolean} isShared - Whether cart is now shared
   */
  toggleButton(isShared) {
    const button = this.element
    
    if (isShared) {
      // Change to unshare button
      button.className = 'btn btn-sm btn-outline-secondary'
      button.innerHTML = this.unshareTextValue
      button.dataset.cartShareShareParam = 'false'
    } else {
      // Change to share button
      button.className = 'btn btn-sm btn-outline-primary'
      button.innerHTML = this.shareTextValue
      button.dataset.cartShareShareParam = 'true'
    }
  }

  /**
   * Update the unsave button text based on shared state
   * @param {boolean} isShared - Whether cart is now shared
   */
  updateUnsaveButton(isShared) {
    const cardContainer = this.element.closest('.bg-white')
    if (!cardContainer) return
    
    // Find the unsave button (btn-outline-danger inside a form)
    const unsaveButton = cardContainer.querySelector('form .btn-outline-danger')
    if (!unsaveButton) return
    
    // Only update if we have the text values available
    if (this.hasUnsaveTextValue && this.hasUnsaveAndUnshareTextValue) {
      unsaveButton.textContent = isShared ? this.unsaveAndUnshareTextValue : this.unsaveTextValue
    }
  }

  /**
   * Handle error response
   * @param {Object} data - Error data
   */
  handleError(data) {
    const errorMessage = data && data.error ? data.error : 'Error updating cart'
    this.showFlashMessage(errorMessage, 'danger')
  }

  /**
   * Display flash message using centralized utility
   * @param {string} message - Message to display
   * @param {string} type - Message type (success, danger)
   */
  showFlashMessage(message, type = 'success') {
    showFlash({ type, message })
  }

  /**
   * Get CSRF token
   * @returns {string} CSRF token
   */
  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ''
  }
}
